/**
 * Labstep
 *
 * @module grid/NewIndex
 * @desc DataGrid for NewIndex
 */

import type { AgGridReact } from 'ag-grid-react';
import { DataGrid } from 'labstep-web/core/DataGrid';
import { LoaderWithProgress } from 'labstep-web/core/DataGrid/Overlay/LoaderProgress';
import type { IDataGridProps } from 'labstep-web/core/DataGrid/types';
import { useExportProgressContext } from 'labstep-web/grid/Index/components/DataGridExportAsCSV/context';
import { GridNewIndexColumnHeader } from 'labstep-web/grid/NewIndex/components/ColumnHeader';
import {
  useBulkSelect,
  useInfiniteScroll,
  useSideBar,
} from 'labstep-web/grid/NewIndex/hooks';
import { GridNewIndexService } from 'labstep-web/grid/NewIndex/services/grid-new-index.service';
import { GridService } from 'labstep-web/grid/services/grid.service';
import type React from 'react';
import { useCallback, useEffect, useMemo } from 'react';
import type { GridNewIndexProps } from './types';

export const GridNewIndex: React.FC<GridNewIndexProps> = ({
  entities,
  loadMore,
  status,
  entityView,
  ...rest
}) => {
  // Infinite scroll, enabled by passing a loadMore function
  const infiniteScrollProps = useInfiniteScroll(loadMore);

  // Bulk select on row selection
  const bulkSelectProps = useBulkSelect();

  // Initialise side bar
  useSideBar(entityView);

  // Set column header custom component
  const components = useMemo(
    () => ({
      agColumnHeader: GridNewIndexColumnHeader,
    }),
    [],
  );

  // Get the row id from the idAttr field - Required for AG Grid
  const getRowId = useCallback<
    NonNullable<IDataGridProps['getRowId']>
  >((params) => params.data.idAttr, []);

  const exportProgressContext = useExportProgressContext();

  useEffect(() => {
    if (exportProgressContext) {
      exportProgressContext.setExportProgress((prev) => ({
        ...prev,
        initialEntitiesCount: entities.length,
      }));
    }
  }, [entities.length]);

  return (
    <DataGrid
      key={`${entityView.id}_${entityView.column_definition_ids?.join('_')}_${entityView.context}`}
      columnDefs={GridNewIndexService.getColDefs(entityView)}
      getRowId={getRowId}
      rowData={entities}
      status={status}
      components={components}
      {...infiniteScrollProps}
      {...bulkSelectProps}
      onColumnResized={(params) => {
        const gridService = new GridService(
          params as unknown as AgGridReact,
        );
        entityView.state = gridService.getState();
        gridService.setState(entityView.state);
      }}
      onGridReady={(params): void => {
        infiniteScrollProps.onGridReady?.(params);
        const gridService = new GridService(
          params as unknown as AgGridReact,
        );
        gridService.setState(entityView.state);
      }}
      hidden={entities.length === 0}
      {...rest}
      progressComponent={
        exportProgressContext &&
        exportProgressContext.exportProgress.isExporting && (
          <LoaderWithProgress
            value={exportProgressContext.exportProgress.value}
            total={exportProgressContext.exportProgress.total}
            hasFailed={exportProgressContext.exportProgress.hasFailed}
          />
        )
      }
    />
  );
};
